import React, { useCallback, useMemo } from "react";
import { GraphResult, GraphValue, ParameterInfo, RowElasticModel, SensitivityStudyResultModel } from "../../../swagger-clients/sensitivity-studies-api-clients.service";
import { getSensitivityStudiesClient } from "../../../services/sensitivity-studies.service";
import { processServerError } from "../../../utils/helpers/error.helper";
import { IPlotly, IPlotlyData } from "../../../utils/shared.types";
import Plot from 'react-plotly.js';
import { Field, Select, Spinner } from "@fluentui/react-components";
import { FilterParameterType, FilterResultModel } from "../../../swagger-clients/s365-dashboard-v2-api-clients.service";
import { getParameterInfo, ParameterData } from "../../../components/parameter-display/parameter-display.component";
import { getSignificantFigures } from "../../../utils/helpers/significant-figures";
import { DispatcherJobDetails } from "../../../components/dispatcher-job-details/dispatcher-job-details.component";

type StudyGraphsTabProps = {
    selectedStudy: SensitivityStudyResultModel;
    filter: FilterResultModel;
}

export type StudyGraphsTabType = {
    getGraphData(): void
}

type ParameterGraphData = {
    parameterInfo: ParameterData;
    plot: IPlotly;
}

type SelectOption = {
    value: string;
    label: string;
}
type GraphParameterData = ParameterData & {
    isFormula?: boolean;
    formulaAlias?: string;
}


export const StudyGraphsTab = React.forwardRef<StudyGraphsTabType, StudyGraphsTabProps>((props, ref) => {

    React.useImperativeHandle(
        ref,
        () => ({
            getGraphData() {
                getGraphData();
            }
        }));
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [studyData, setStudyData] = React.useState<RowElasticModel[]>([]);
    const [parameterData, setParameterData] = React.useState<GraphParameterData[]>([]);
    const [parameterResults, setParameterResults] = React.useState<ParameterGraphData[]>([]);
    const [selectedParameterId, setSelectedParameterId] = React.useState<string>(null);
    const [parameterOptions, setParameterOptions] = React.useState<SelectOption[]>([]);
    const [plotWidth, setPlotWidth] = React.useState<number>(window.innerWidth - 300);

    const [selectedDispatcherJobId, setSelectedDispatcherJobId] = React.useState<number>();
    const [showCalculationJobDetails, setShowCalculationJobDetails] = React.useState<boolean>(false);

    React.useEffect(() => {
        const handleResize = () => {
            setPlotWidth(window.innerWidth - 300);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);



    React.useEffect(() => {
        if (!!props.filter) {
            getFilterParameterDetails();
        }
    }, [props.filter]);

    React.useEffect(() => {
        if (!!parameterData && parameterData.length > 0) {
            getGraphData();
        }

    }, [parameterData]);

    React.useEffect(() => {
        if (!!parameterResults) {
            const updatedParameterResults = parameterResults.map(x =>
                ({ ...x, plot: { ...x.plot, layout: { ...x.plot.layout, width: plotWidth } } } as ParameterGraphData));
            setParameterResults(updatedParameterResults);
        }

    }, [plotWidth]);



    const getGraphData = async () => {
        try {
            setIsLoading(true);
            const client = getSensitivityStudiesClient();
            const data = await client.getStudyGraphData(props.selectedStudy.id, props.selectedStudy.currentVersionId);
            setStudyData(data);
            const mappedParameterData = mapGraphResultToIPlotly(data);
            setParameterResults(mappedParameterData);
            //console.log("getGraphData mappedParameterData", mappedParameterData);

            // select first parameter
            if (!!mappedParameterData && mappedParameterData.length > 0) {
                const paramId = mappedParameterData[0].parameterInfo.id.split('_')[1];
                setSelectedParameterId(paramId);
            }

        } catch (error) {
            processServerError(error, undefined, "An error occurred while loading graph data.");

        } finally {
            setIsLoading(false);
        }
    }

    const mapGraphResultToIPlotly = (data: RowElasticModel[]): ParameterGraphData[] => {
        let result: ParameterGraphData[] = [];

        if (!data || data.length == 0)
            return [];
        const rowParametersData = getRowParameterData(data[0]);


        const inputParams = rowParametersData.filter(x => x.id.startsWith("in_"));
        //  console.log("inputParams", inputParams);
        // console.log("data", data);
        inputParams.forEach(param => {
            const title = getParameterName(param);
            const paramValues = data.map(x => {
                return Object.entries(x.parameters)
                    .filter(([key, value]) => key.startsWith("in_") && `in_${value.sensitivityStudyParameterId}` == param.id)
                    .map(([key, value]) => value.value)[0];

            });
            const optimizationFunctionValues = data.map(x => x.evaluationFunctionValue);
            let plot: IPlotly = {
                data: [
                    {
                        x: paramValues,
                        y: optimizationFunctionValues,
                        type: 'scatter',
                        mode: 'markers',
                        marker: { color: 'var(--colorCompoundBrandStroke)' },
                        name: "All values",
                        hoverinfo: 'text',
                        text: data.map(x => getPopoverText(x)),
                    }
                ],
                layout: {
                    autosize: true,
                    resposive: true,
                    title: "",
                    width: plotWidth,
                    xaxis: {
                        title: { text: title },
                    },
                    yaxis: {
                        title: { text: props.selectedStudy.optimizationFunctionAlias ?? "Optimization function" }
                    }


                }
            };

            result.push({ parameterInfo: param, plot: plot });

        });

        return result;
    }
    const getPopoverText = (value: RowElasticModel) => {

        const optimizationFunctionName = props.selectedStudy.optimizationFunctionAlias ?? "Optimization function";
        let result = ` Row ID: ${value.id}<br>`;

        Object.entries(value.parameters).forEach(([key, value]) => {
            const isInput = key.startsWith("in_");
            const paramId = `${isInput ? "in_" : "out_"}${value.sensitivityStudyParameterId}`;
            const param = parameterData.find(x => x.id == paramId);
            const paramName = getParameterName(param);
            const paramValue = getSignificantFigures(+value.value, 5);
            result += `${paramName}: ${paramValue}<br>`;
        });

        const optimizationFunctionValue = getSignificantFigures(value.evaluationFunctionValue, 5);
        result += ` ${optimizationFunctionName}: ${optimizationFunctionValue}`;
        return result;
    }

    const getFilterParameterDetails = async () => {
        if (!props.filter)
            return [];

        let parameterDetails: GraphParameterData[] = [];

        if (!!props.filter.inputFilterParameters) {
            for (let i = 0; i < props.filter.inputFilterParameters.length; i++) {
                const param = props.filter.inputFilterParameters[i];
                let paramData: GraphParameterData = undefined;
                if (param.parameterType == FilterParameterType.Formula) {
                    paramData = {
                        isFormula: true,
                        tooltip: param.formula,
                        formulaAlias: param.alias
                    } as GraphParameterData;
                } else {
                    paramData = await getParameterInfo(props.filter.fileUniqueIdentifier, props.filter.fileVersionNumber?.toString(), param.name);
                }
                const paramId = props.selectedStudy.sensitivityStudyInputParameters.find(x => x.inputFilterParameterId == param.id)?.id;
                parameterDetails.push({ ...paramData, id: `in_${paramId}` });
            }
        }

        if (!!props.filter.outputFilterParameters) {
            for (let i = 0; i < props.filter.outputFilterParameters.length; i++) {
                const param = props.filter.outputFilterParameters[i];
                let paramData: GraphParameterData = undefined;
                if (param.parameterType == FilterParameterType.Formula) {
                    paramData = {
                        isFormula: true,
                        tooltip: param.formula,
                        formulaAlias: param.alias
                    } as GraphParameterData;
                } else {
                    paramData = await getParameterInfo(props.filter.fileUniqueIdentifier, props.filter.fileVersionNumber?.toString(), param.name);
                }
                const paramId = props.selectedStudy.sensitivityStudyOutputParameters.find(x => x.outputFilterParameterId == param.id)?.id;
                parameterDetails.push({ ...paramData, id: `out_${paramId}` });
            }
        }

        setParameterData(parameterDetails);

        const dropdownOptions = parameterDetails?.filter(x => x.id.startsWith("in_")).map(x => ({ value: x.id.split('_')[1], label: getParameterName(x) } as SelectOption)) ?? [];
        setParameterOptions(dropdownOptions);

    }

    const getRowParameterData = (row: RowElasticModel) => {
        let parameterDataArray: GraphParameterData[] = [];
        if (!row.parameters)
            return parameterDataArray;
        for (const [key, value] of Object.entries(row.parameters)) {
            const isInput = key.startsWith("in_");
            const paramId = `${isInput ? "in_" : "out_"}${value.sensitivityStudyParameterId}`
            const paramData = parameterData.find(x => x.id == paramId);
            if (!!paramData)
                parameterDataArray.push(paramData);
        }

        return parameterDataArray;

    }

    const getParameterName = (value: GraphParameterData) => {

        if (value.isFormula) {
            return value.formulaAlias;
        }
        let title = '';
        if (value.unitOperation) {
            title = title + `U:${value.unitOperation.displayName}`;
        }
        if (value.stream) {
            title = title + ` S:${value.stream.displayName}`;
        }
        if (value.component) {
            title = title + ` C:${value.component.displayName}`;
        }
        if (value.parameter) {
            title = title + ` P:${value.parameter.displayName}`;
        }
        if (value.parameter?.unit) {
            title = title + ` (${value.parameter.unit})`;
        }
        return title;


    }
    const onPlotlyClick = (data: any) => {
        //console.log("onPlotlyClick", data);
        const pointText = data?.points?.[0]?.text;
        if (!!pointText) {
            const flowsheetRowId = getFlowsheetRowId(pointText);
            const row = studyData?.find(x => x.id == flowsheetRowId);
            if (!!row && !!row.dispatcherJobId) {
                setSelectedDispatcherJobId(row.dispatcherJobId);
                setShowCalculationJobDetails(true);
            }
        }
    }
    const getFlowsheetRowId = (text: string) => {

        const flowsheetIdRegex = /Row ID: (\d+)/;
        const flowsheetIdMatch = text.match(flowsheetIdRegex);


        if (flowsheetIdMatch) {
            return Number(flowsheetIdMatch[1]);
        }
        return undefined;
    }


    const selectedParameterData = useMemo(() => {
        // console.log("selectedParameterData useMemo parameterResults", parameterResults, selectedParameterId);
        return parameterResults?.find(x => x.parameterInfo.id == `in_${selectedParameterId}`);
    }

        , [selectedParameterId, parameterResults, parameterData]);

    return <div>
        {isLoading && <Spinner size="large" />}
        {!isLoading && <div>
            <Field label="Parameter" style={{ marginBottom: "20px" }}>
                <Select
                    value={selectedParameterId}
                    placeholder="Select parameter"
                    style={{ maxWidth: "300px" }}
                    onChange={(ev, data) => { setSelectedParameterId(data.value); }} >
                    {parameterOptions.map((option) => {
                        return <option value={option.value}>
                            {option.label}
                        </option>
                    })}
                </Select>
            </Field>

            <Plot data={selectedParameterData?.plot?.data} layout={selectedParameterData?.plot?.layout} onClick={onPlotlyClick} />
            {showCalculationJobDetails && selectedDispatcherJobId &&
                <DispatcherJobDetails
                    jobId={selectedDispatcherJobId}
                    isOpened={true}
                    onClose={() => setShowCalculationJobDetails(false)}
                />}
        </div>
        }
    </div>
});